import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { EditableSelect, EditableSelectOption } from "@/components/ui/editable-select";
import { useState } from "react";

export interface EditableTableColumn<T> {
  key: keyof T | string;
  header: string;
  type: 'text' | 'select' | 'currency' | 'calculated';
  options?: EditableSelectOption<string>[];
  formula?: (row: T) => number;
  subtype?: 'currency' | 'number'; // New subtype field
}

interface EditingCell<T> {
  id: number;
  field: keyof T | string;
  value: string;
}

export interface EditableTableProps<T extends { id: number }> {
  data: T[];
  columns: EditableTableColumn<T>[];
  onUpdate: (id: number, field: keyof T | string, value: string) => Promise<void>;
  onDelete?: (id: number) => Promise<void>;
}

export function EditableTable<T extends { id: number }>({ 
  data, 
  columns, 
  onUpdate,
  onDelete 
}: EditableTableProps<T>) {
  const [editingCell, setEditingCell] = useState<EditingCell<T> | null>(null);
  const [editValue, setEditValue] = useState<string>("");

  const startEditing = (id: number, field: keyof T | string, value: any) => {
    setEditingCell({ id, field, value: String(value) });
    setEditValue(String(value));
  };

  const cancelEditing = () => {
    setEditingCell(null);
    setEditValue("");
  };

  const handleSave = async (value?: string) => {
    if (!editingCell) return;
    
    const valueToSave = value ?? editValue;
    await onUpdate(editingCell.id, editingCell.field, valueToSave);
    cancelEditing();
  };

  const renderCell = (item: T, column: EditableTableColumn<T>) => {
    const isEditing = editingCell?.id === item.id && editingCell?.field === column.key;
    const value = String(item[column.key as keyof T]);

    if (column.type === 'calculated' && column.formula) {
      const calculatedValue = column.formula(item);
      if (column.subtype === 'currency') {
        const formattedValue = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        }).format(calculatedValue);
        return <TableCell>{formattedValue}</TableCell>;
      }
      return <TableCell>{calculatedValue}</TableCell>;
    }

    if (column.type === 'select' && column.options) {
      return (
        <EditableSelect
          value={value}
          options={column.options}
          isEditing={isEditing}
          onStartEdit={() => startEditing(item.id, column.key as keyof T, value)}
          onSave={handleSave}
          onCancel={cancelEditing}
        />
      );
    }

    if (column.type === 'currency') {
      const formattedValue = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(Number(value));

      if (isEditing) {
        return (
          <Input
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            className="h-8"
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSave();
              if (e.key === "Escape") cancelEditing();
            }}
            autoFocus
          />
        );
      }

      return (
        <div
          className="cursor-pointer hover:bg-accent hover:text-accent-foreground p-2 rounded"
          onClick={() => startEditing(item.id, column.key, value)}
        >
          {formattedValue}
        </div>
      );
    }

    if (isEditing) {
      return (
        <Input
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          className="h-8"
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSave();
            if (e.key === "Escape") cancelEditing();
          }}
          autoFocus
        />
      );
    }

    return (
      <div
        className="cursor-pointer hover:bg-accent hover:text-accent-foreground p-2 rounded"
        onClick={() => startEditing(item.id, column.key, value)}
      >
        {value}
      </div>
    );
  };

  return (
    <Table>
      <TableHeader>
        <TableRow>
          {columns.map((column) => (
            <TableHead key={String(column.key)}>{column.header}</TableHead>
          ))}
          {onDelete && <TableHead className="text-right">Actions</TableHead>}
        </TableRow>
      </TableHeader>
      <TableBody>
        {data.map((item) => (
          <TableRow key={item.id}>
            {columns.map((column) => (
              <TableCell key={`${item.id}-${String(column.key)}`}>
                {renderCell(item, column)}
              </TableCell>
            ))}
            {onDelete && (
              <TableCell className="text-right">
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => onDelete(item.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}